/**
 * Receipt capture + on-device OCR helpers.
 *
 * Known limitations:
 *   - ML Kit text recognition is native-only — `extractReceiptText`
 *     throws on web. The Scan button is hidden by `supportsOCR()` in
 *     web builds so users never see it.
 *   - Accuracy depends on image quality, lighting, and receipt
 *     condition. The review form lets the user fix any field manually.
 *   - Date parsing is English-locale only (M/D/Y formats). Ambiguous
 *     dates default to month-first.
 *   - Amount detection is optimized for US dollar format (`$XX.XX`).
 */

import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { getCapacitorPlugin, isNative } from './platform';

interface TextRecognitionPlugin {
  recognizeText(options: {
    image: { base64String: string };
  }): Promise<{ text: string }>;
}

/**
 * Open the device camera (or web file picker) and return the captured
 * image as a base64 data URL. Returns null on cancel or any error so
 * the caller can silently no-op.
 *
 * Native: routes through @capacitor/camera at quality 80, DataUrl.
 * Web:    creates an input[type=file][accept=image/*][capture=environment]
 *         element, prompting mobile browsers to default to the rear
 *         camera while still working as a plain file picker on desktop.
 */
export async function captureReceiptImage(): Promise<string | null> {
  if (isNative()) {
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        quality: 80,
        allowEditing: false,
      });
      return photo.dataUrl ?? null;
    } catch (err) {
      console.warn('[ocr] native camera capture failed', err);
      return null;
    }
  }
  return captureViaFileInput();
}

/**
 * Web fallback. Programmatically mounts an <input type="file"> with
 * the camera-capture hint, awaits the user's selection, and reads the
 * chosen file as a data URL. Resolves null if the user cancels.
 */
function captureViaFileInput(): Promise<string | null> {
  if (typeof document === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    // `capture` is a hint — mobile browsers default to the rear camera;
    // desktop browsers ignore it and show a normal file picker.
    input.setAttribute('capture', 'environment');
    input.style.position = 'fixed';
    input.style.left = '-9999px';

    let settled = false;
    const settle = (v: string | null) => {
      if (settled) return;
      settled = true;
      try {
        document.body.removeChild(input);
      } catch {
        /* already detached */
      }
      resolve(v);
    };

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        settle(null);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        settle(typeof result === 'string' ? result : null);
      };
      reader.onerror = () => settle(null);
      reader.readAsDataURL(file);
    };

    // Cancel detection — the modern `cancel` event fires when the user
    // dismisses the dialog. Browsers without it just leave the input
    // unused and the user re-taps; not ideal but acceptable.
    input.addEventListener('cancel', () => settle(null));

    document.body.appendChild(input);
    input.click();
  });
}

/**
 * Run on-device text recognition against a base64 data URL. Native
 * only — accesses the @capacitor-mlkit/text-recognition plugin via
 * Capacitor's runtime registry, so the bundle still builds when the
 * plugin isn't installed.
 */
export async function extractReceiptText(imageB64: string): Promise<string> {
  if (!isNative()) {
    throw new Error('OCR is only available on iOS / Android.');
  }
  const plugin = getCapacitorPlugin<TextRecognitionPlugin>('TextRecognition');
  if (!plugin || typeof plugin.recognizeText !== 'function') {
    throw new Error(
      'TextRecognition plugin not registered. Install @capacitor-mlkit/text-recognition and run cap sync.',
    );
  }
  // Strip data URL prefix; ML Kit expects the raw base64 body.
  const base64String = imageB64.includes(',')
    ? imageB64.slice(imageB64.indexOf(',') + 1)
    : imageB64;
  const result = await plugin.recognizeText({ image: { base64String } });
  return typeof result?.text === 'string' ? result.text : '';
}

export interface ParsedReceipt {
  merchant: string | null;
  amount: number | null;
  date: string | null;
  rawText: string;
}

const NOISE_PREFIXES = [
  /^WELCOME TO\s+/i,
  /^THANK YOU FROM\s+/i,
  /^THANK YOU FOR SHOPPING AT\s+/i,
  /^THANKS FOR SHOPPING AT\s+/i,
];

const DATE_RES: { re: RegExp; year: 'short' | 'long' | 'iso' }[] = [
  { re: /\b(\d{4})-(\d{1,2})-(\d{1,2})\b/, year: 'iso' },
  { re: /\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})\b/, year: 'long' },
  { re: /\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2})\b/, year: 'short' },
];

const TOTAL_KEYWORDS = /\b(total|amount due|balance due|grand total|amt)\b/i;
const DOLLAR_RE = /\$?\s*([0-9]{1,5}(?:[.,][0-9]{2}))/g;

function titleCase(s: string): string {
  return s
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

function isPureDigitsOrPunct(s: string): boolean {
  return /^[\d\s.,\-#:/$]+$/.test(s);
}

function isDateLike(s: string): boolean {
  return DATE_RES.some(({ re }) => re.test(s));
}

function pickMerchant(text: string): string | null {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  for (let i = 0; i < Math.min(5, lines.length); i++) {
    let line = lines[i]!;
    for (const re of NOISE_PREFIXES) {
      line = line.replace(re, '');
    }
    line = line.trim();
    if (!line) continue;
    if (isPureDigitsOrPunct(line)) continue;
    if (isDateLike(line)) continue;
    const letters = line.replace(/[^a-zA-Z]/g, '');
    if (letters.length < 3) continue;
    return titleCase(line);
  }
  return null;
}

function parseDollarsFromLine(line: string): number[] {
  const out: number[] = [];
  const re = new RegExp(DOLLAR_RE.source, DOLLAR_RE.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(line)) !== null) {
    const raw = m[1]!.replace(',', '.');
    const v = Number(raw);
    if (Number.isFinite(v) && v > 0) out.push(v);
  }
  return out;
}

function pickAmount(text: string): number | null {
  const lines = text.split(/\r?\n/);
  const totalLineAmounts: number[] = [];
  for (const line of lines) {
    if (TOTAL_KEYWORDS.test(line)) {
      totalLineAmounts.push(...parseDollarsFromLine(line));
    }
  }
  if (totalLineAmounts.length > 0) {
    return Math.max(...totalLineAmounts);
  }
  const all: number[] = [];
  for (const line of lines) {
    for (const v of parseDollarsFromLine(line)) {
      // Reject account-number-like amounts.
      if (v < 10000) all.push(v);
    }
  }
  return all.length ? Math.max(...all) : null;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function pickDate(text: string): string | null {
  for (const line of text.split(/\r?\n/)) {
    for (const { re, year } of DATE_RES) {
      const m = line.match(re);
      if (!m) continue;
      let y: number;
      let mo: number;
      let d: number;
      if (year === 'iso') {
        y = Number(m[1]);
        mo = Number(m[2]);
        d = Number(m[3]);
      } else {
        mo = Number(m[1]);
        d = Number(m[2]);
        const yRaw = Number(m[3]);
        y = year === 'short' ? 2000 + yRaw : yRaw;
      }
      if (mo < 1 || mo > 12) continue;
      if (d < 1 || d > 31) continue;
      if (y < 2000 || y > 2099) continue;
      return `${y}-${pad2(mo)}-${pad2(d)}`;
    }
  }
  return null;
}

export function parseReceipt(text: string): ParsedReceipt {
  return {
    merchant: pickMerchant(text),
    amount: pickAmount(text),
    date: pickDate(text),
    rawText: text,
  };
}
